import React, { useMemo, useState } from 'react';
import { Bug, ChevronDown, ChevronRight, Layers, Target, Tag, GitBranch, Braces } from 'lucide-react';
import { Message, PartResult } from './types';
import { JsonTreeNode } from '../../common/JsonTreeViewer';

/**
 * Admin-only debug panel attached to a bot message. Surfaces the explainability
 * signals that already arrive with each message (probability scores, the
 * candidates the model weighed, parts resolution, context tags, raw event
 * trace) but are hidden from the normal customer-facing UI. Rendered only when
 * the viewer is an admin AND debug mode is on (gated by the caller).
 */

/** Bucket a 0–1 confidence into a label + tailwind colors for the bar/badge. */
function scoreVisual(score: number): { label: string; bar: string; text: string; bg: string } {
  if (score >= 0.8) return { label: 'High', bar: 'bg-emerald-500', text: 'text-emerald-700', bg: 'bg-emerald-50' };
  if (score >= 0.55) return { label: 'Medium', bar: 'bg-amber-500', text: 'text-amber-700', bg: 'bg-amber-50' };
  return { label: 'Low', bar: 'bg-rose-500', text: 'text-rose-700', bg: 'bg-rose-50' };
}

const Section: React.FC<{ icon: React.ReactNode; title: string; children: React.ReactNode }> = ({ icon, title, children }) => (
  <div className="px-3 py-2.5 border-t border-slate-200/70 first:border-t-0">
    <div className="flex items-center gap-1.5 mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
      {icon}
      <span>{title}</span>
    </div>
    {children}
  </div>
);

const Chip: React.FC<{ children: React.ReactNode; tone?: 'default' | 'danger' }> = ({ children, tone = 'default' }) => (
  <span
    className={`inline-block text-[11px] font-medium px-2 py-0.5 rounded-full border ${
      tone === 'danger'
        ? 'bg-rose-50 text-rose-700 border-rose-200'
        : 'bg-slate-100 text-slate-700 border-slate-200'
    }`}
  >
    {children}
  </span>
);

const PartScoreRow: React.FC<{ part: PartResult; index: number }> = ({ part, index }) => {
  const name =
    part.related_corrected_name || part.part_name || part.partId || `Part ${index + 1}`;
  const hasScore = typeof part.probability_score === 'number';
  const score = hasScore ? Math.max(0, Math.min(1, part.probability_score as number)) : null;
  const vis = score !== null ? scoreVisual(score) : null;

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-2.5">
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <div className="min-w-0">
          <div className="text-xs font-semibold text-slate-800 truncate">{name}</div>
          {part.partId && <div className="text-[10px] font-mono text-slate-400 truncate">{part.partId}</div>}
        </div>
        {vis && (
          <span className={`shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded-full ${vis.bg} ${vis.text}`}>
            {Math.round((score as number) * 100)}% · {vis.label}
          </span>
        )}
      </div>

      {vis && (
        <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden mb-1.5">
          <div className={`h-full rounded-full ${vis.bar}`} style={{ width: `${Math.round((score as number) * 100)}%` }} />
        </div>
      )}

      {!hasScore && <div className="text-[11px] text-slate-400 italic">no probability score returned</div>}

      {/* Fallback ladder — explains how the catalog match was reached */}
      {(part.fallback_attempt != null || part.fallback_note) && (
        <div className="mt-1 text-[11px] text-slate-600">
          <span className="font-medium text-slate-500">Search path: </span>
          {part.fallback_attempt != null && <span className="font-mono">step {part.fallback_attempt}</span>}
          {part.fallback_note && <span> — {part.fallback_note}</span>}
        </div>
      )}

      {/* Why the score was capped */}
      {part.score_capped_dimensions && part.score_capped_dimensions.length > 0 && (
        <div className="mt-1 text-[11px] text-amber-700">
          <span className="font-medium">Score capped — unresolved: </span>
          {part.score_capped_dimensions.join(', ')}
        </div>
      )}

      {/* Implicit assumptions the agent made */}
      {part.resolutions && part.resolutions.length > 0 && (
        <ul className="mt-1 list-disc list-inside text-[11px] text-slate-600 space-y-0.5">
          {part.resolutions.map((r, i) => <li key={i}>{r}</li>)}
        </ul>
      )}

      {/* Candidates the model considered but did not pick */}
      {part.alternatives && part.alternatives.length > 0 && (
        <div className="mt-2">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1">
            Alternatives weighed ({part.alternatives.length})
          </div>
          <div className="space-y-1">
            {part.alternatives.map((alt, i) => (
              <div key={i} className="text-[11px] bg-slate-50 rounded px-2 py-1 border border-slate-100">
                <span className="font-mono text-slate-700">{alt.partId}</span>
                {alt.partName && <span className="text-slate-600"> · {alt.partName}</span>}
                {alt.reason && <div className="text-slate-500 mt-0.5">↳ {alt.reason}</div>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const MessageDebugPanel: React.FC<{ message: Message }> = ({ message }) => {
  const [open, setOpen] = useState(false);

  const parts = message.parts_results ?? [];

  // Curated raw object for the JSON tree — only the fields useful for explaining
  // the result, so the tree isn't drowned in render bookkeeping.
  const rawState = useMemo(() => {
    const obj: Record<string, any> = {};
    if (parts.length) obj.parts_results = parts;
    if (message.requested_parts) obj.requested_parts = message.requested_parts;
    if (message.not_found_parts) obj.not_found_parts = message.not_found_parts;
    if (message.context_tags) obj.context_tags = message.context_tags;
    if (message.search_summarization) obj.search_summarization = message.search_summarization;
    if (message.ext_search_results) obj.ext_search_results = message.ext_search_results;
    if (message.repair_instructions) obj.repair_instructions = message.repair_instructions;
    if (message.intentPreview) obj.intentPreview = message.intentPreview;
    if (message.events && message.events.length) obj.events = message.events;
    // The raw assessment payload (often JSON the bubble parsed away)
    if (message.text) obj.raw_text = message.text;
    return obj;
  }, [message, parts]);

  const requested = message.requested_parts ?? [];
  const notFound = message.not_found_parts ?? [];
  const tags = message.context_tags ?? [];
  const hasAnything =
    parts.length > 0 ||
    requested.length > 0 ||
    notFound.length > 0 ||
    tags.length > 0 ||
    Object.keys(rawState).length > 0;

  if (!hasAnything) return null;

  return (
    <div className="mt-2 rounded-xl border border-indigo-200 bg-indigo-50/40 overflow-hidden text-left">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-indigo-100/50 transition-colors"
      >
        {open ? <ChevronDown className="w-3.5 h-3.5 text-indigo-500" /> : <ChevronRight className="w-3.5 h-3.5 text-indigo-500" />}
        <Bug className="w-3.5 h-3.5 text-indigo-600" />
        <span className="text-[11px] font-semibold uppercase tracking-wide text-indigo-700">
          Debug · model reasoning
        </span>
        {!open && parts.length > 0 && (
          <span className="ml-auto text-[10px] text-indigo-500 font-medium">
            {parts.length} part{parts.length === 1 ? '' : 's'} scored
          </span>
        )}
      </button>

      {open && (
        <div className="bg-white/70">
          {/* Confidence / probability scores */}
          {parts.length > 0 && (
            <Section icon={<Target className="w-3 h-3" />} title="Confidence per part">
              <div className="space-y-2">
                {parts.map((p, i) => <PartScoreRow key={i} part={p} index={i} />)}
              </div>
            </Section>
          )}

          {/* Parts resolution */}
          {(requested.length > 0 || notFound.length > 0) && (
            <Section icon={<Layers className="w-3 h-3" />} title="Parts resolution">
              {requested.length > 0 && (
                <div className="mb-2">
                  <div className="text-[10px] text-slate-400 mb-1">Requested</div>
                  <div className="flex flex-wrap gap-1">{requested.map((p, i) => <Chip key={i}>{p}</Chip>)}</div>
                </div>
              )}
              {notFound.length > 0 && (
                <div>
                  <div className="text-[10px] text-slate-400 mb-1">Not found in catalog</div>
                  <div className="flex flex-wrap gap-1">{notFound.map((p, i) => <Chip key={i} tone="danger">{p}</Chip>)}</div>
                </div>
              )}
            </Section>
          )}

          {/* Context tags */}
          {tags.length > 0 && (
            <Section icon={<Tag className="w-3 h-3" />} title="Context tags">
              <div className="flex flex-wrap gap-1">{tags.map((tg, i) => <Chip key={i}>{tg}</Chip>)}</div>
            </Section>
          )}

          {/* Intent preview */}
          {message.intentPreview && (
            <Section icon={<GitBranch className="w-3 h-3" />} title="Intent preview">
              <div className="text-[11px] text-slate-600 italic">{message.intentPreview}</div>
            </Section>
          )}

          {/* Raw state JSON tree */}
          <Section icon={<Braces className="w-3 h-3" />} title="Raw state">
            <div className="rounded-lg border border-slate-200 bg-slate-50 max-h-72 overflow-y-auto p-2">
              {Object.entries(rawState).map(([k, v]) => (
                <JsonTreeNode key={k} nodeKey={k} value={v} depth={0} defaultExpanded={false} />
              ))}
            </div>
          </Section>
        </div>
      )}
    </div>
  );
};

export default MessageDebugPanel;
